import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  History,
  RefreshCw,
  Siren,
  Timer,
  Undo2,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { useNow } from "../gpu-fleet/useNow";
import {
  DEFAULT_OVERDUE_MINUTES,
  DEFAULT_UNRECOVERED_HOURS,
  HISTORY_WINDOWS,
  INCIDENTS_PATH,
  OPEN_LOOKBACK_DAYS,
  OUTCOME_WEEKS,
  causeBreakdown,
  causeVariant,
  customerDetail,
  customerName,
  customerNotice,
  echoLabel,
  environmentLabel,
  explanationLine,
  formatAge,
  formatMinutes,
  formatTimestamp,
  incidentCauseDescription,
  incidentCauseLabel,
  incidentLinks,
  incidentsErrorKind,
  occasionLabel,
  outcomeTotalsRow,
  outcomeWeekRows,
  readStoredWindow,
  recoveryView,
  resolutionLabel,
  shortWorkloadId,
  sortHistory,
  sortOpenIncidents,
  splitOpenByWindow,
  stateLabel,
  stateVariant,
  storeWindow,
  timeToFilmMinutes,
  waitingSince,
  windowLabel,
  windowPhrase,
  windowState,
  type HistoryWindow,
  type OutcomeWeekRow,
} from "./incidentsModel";
import type { EchoIncident, EchoIncidentSummary } from "./types";
import { useIncidentList, useIncidentSummary } from "./useIncidents";

/**
 * Administrator-only view of stuck Echoes: approved films that failed, were
 * rejected by WhatsApp or are overdue, whether each customer got a film in the
 * end, and the weekly share of customers left without one after 72 hours.
 * The same incidents post to #felix-alerts; this page is the full record.
 */
export function IncidentsPage() {
  const [windowDays, setWindowDays] = useState<HistoryWindow>(() =>
    readStoredWindow(),
  );
  const [showOlderOpen, setShowOlderOpen] = useState(false);
  const now = useNow(30_000).getTime();
  const summary = useIncidentSummary(windowDays, OUTCOME_WEEKS);
  const open = useIncidentList("open", OPEN_LOOKBACK_DAYS);
  const history = useIncidentList("all", windowDays);

  const errors = [summary.error, open.error, history.error];
  const disabled = errors.some(
    (error) => error && incidentsErrorKind(error) === "disabled",
  );
  const forbidden = errors.some(
    (error) => error && incidentsErrorKind(error) === "forbidden",
  );
  const overdueMinutes =
    summary.data?.policy?.overdue_after_minutes ?? DEFAULT_OVERDUE_MINUTES;
  const unrecoveredHours =
    summary.data?.policy?.unrecovered_after_hours ?? DEFAULT_UNRECOVERED_HOURS;
  const environment = environmentLabel(
    summary.data?.policy_ref?.environment ?? open.data?.policy_ref?.environment,
  );
  const fetching = summary.isFetching || open.isFetching || history.isFetching;
  const openNow = splitOpenByWindow(
    sortOpenIncidents(open.data?.incidents ?? []),
    windowDays,
    now,
  );
  const shownOpen = showOlderOpen
    ? [...openNow.older, ...openNow.inWindow]
    : openNow.inWindow;
  const refresh = () => {
    void summary.refetch();
    void open.refetch();
    void history.refetch();
  };

  return (
    <main className="mx-auto w-full max-w-7xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Siren className="size-6 text-muted-foreground" aria-hidden />
            Incidents
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {explanationLine(overdueMinutes)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            variant="outline"
            value={String(windowDays)}
            onValueChange={(next) => {
              if (!next) return;
              const days = Number(next) as HistoryWindow;
              setWindowDays(days);
              storeWindow(days);
            }}
            aria-label="Time window"
          >
            {HISTORY_WINDOWS.map((days) => (
              <ToggleGroupItem
                key={days}
                value={String(days)}
                className="px-2.5 text-xs"
                title={`Incidents that opened or ended in ${windowPhrase(days)}`}
              >
                {windowLabel(days)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={fetching || forbidden}
          >
            <RefreshCw
              className={cn("mr-2 size-4", fetching && "animate-spin")}
            />
            Refresh
          </Button>
        </div>
      </div>

      {disabled ? (
        <Alert>
          <AlertTriangle />
          <AlertTitle>
            Incidents are not enabled in this environment yet
          </AlertTitle>
          <AlertDescription>
            <p>
              The dashboard API reports the incident monitor as off here. It
              turns on when the <code>dashboard.echo_incidents.policy.v1</code>{" "}
              runtime policy enables exposure for this environment. This page
              checks again every minute.
            </p>
          </AlertDescription>
        </Alert>
      ) : forbidden ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Administrator access required</AlertTitle>
          <AlertDescription>
            <p>
              Incidents include customer details, so only CRM administrators can
              open this page.
            </p>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {[
              environment,
              summary.data
                ? `Updated ${formatTimestamp(summary.data.generated_at)}`
                : null,
              summary.data?.policy_ref?.config_release_id
                ? `Policy release ${summary.data.policy_ref.config_release_id}`
                : null,
              "Refreshes every minute",
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>

          <SummaryTiles
            summary={summary.data}
            isPending={summary.isPending}
            error={summary.error}
            days={windowDays}
            unrecoveredHours={unrecoveredHours}
          />

          <SectionCard
            title={
              showOlderOpen && openNow.older.length > 0
                ? "Open now · any age"
                : `Open now · opened in ${windowPhrase(windowDays)}`
            }
            icon={<Siren className="size-4" />}
            count={open.data ? shownOpen.length : null}
          >
            {open.isPending ? (
              <TableSkeleton />
            ) : open.error && !open.data ? (
              <SectionError error={open.error} />
            ) : (
              <>
                <OpenIncidentsTable
                  incidents={shownOpen}
                  now={now}
                  overdueMinutes={overdueMinutes}
                  windowDays={windowDays}
                />
                {openNow.older.length > 0 ? (
                  <OlderOpenNotice
                    count={openNow.older.length}
                    windowDays={windowDays}
                    shown={showOlderOpen}
                    onToggle={() => setShowOlderOpen((shown) => !shown)}
                  />
                ) : null}
              </>
            )}
          </SectionCard>

          <SectionCard
            title={`Resolved & unrecovered · ${windowPhrase(windowDays)}`}
            icon={<History className="size-4" />}
            count={
              history.data ? sortHistory(history.data.incidents).length : null
            }
          >
            {history.isPending ? (
              <TableSkeleton />
            ) : history.error && !history.data ? (
              <SectionError error={history.error} />
            ) : (
              <HistoryTable
                incidents={sortHistory(history.data?.incidents ?? [])}
                overdueMinutes={overdueMinutes}
              />
            )}
          </SectionCard>

          <SectionCard
            title="Customers without a film within 72 h"
            icon={<Users className="size-4" />}
          >
            {summary.isPending ? (
              <TableSkeleton />
            ) : summary.error && !summary.data ? (
              <SectionError error={summary.error} />
            ) : (
              <OutcomesTable summary={summary.data} />
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Each customer is counted once per week, in the week of their
              approval. A customer is without a film when an approval that week
              was not followed by a film within {unrecoveredHours} hours.
              Pending means approved less than {unrecoveredHours} hours ago, so
              not decided yet; the rate is without ÷ (approved − pending).
            </p>
          </SectionCard>
        </>
      )}
    </main>
  );
}

IncidentsPage.path = INCIDENTS_PATH;

// --- Tiles -------------------------------------------------------------------

function SummaryTiles({
  summary,
  isPending,
  error,
  days,
  unrecoveredHours,
}: {
  summary: EchoIncidentSummary | undefined;
  isPending: boolean;
  error: unknown;
  days: number;
  unrecoveredHours: number;
}) {
  const tiles = summary?.tiles;
  const unavailable = !tiles && !isPending && Boolean(error);
  const value = (count: number | null | undefined) =>
    count == null ? "—" : count.toLocaleString("en-US");
  const causes = causeBreakdown(summary?.causes);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Tile
          icon={<Siren className="size-4" />}
          label="Open now"
          value={value(tiles?.open_now)}
          sublabel="waiting for a film, any age"
          tone={tiles && tiles.open_now > 0 ? "alert" : "default"}
          isPending={isPending}
          unavailable={unavailable}
        />
        <Tile
          icon={<AlertTriangle className="size-4" />}
          label={`Opened (${windowLabel(days)})`}
          value={value(tiles?.opened)}
          sublabel="approved Echoes that got stuck"
          isPending={isPending}
          unavailable={unavailable}
        />
        <Tile
          icon={<CheckCircle2 className="size-4" />}
          label="Recovered — same Echo"
          value={value(tiles?.resolved_same_echo)}
          sublabel="retry or re-drive delivered it"
          isPending={isPending}
          unavailable={unavailable}
        />
        <Tile
          icon={<Undo2 className="size-4" />}
          label="Recovered — later Echo"
          value={value(tiles?.resolved_later_echo)}
          sublabel="a new Echo reached the customer"
          isPending={isPending}
          unavailable={unavailable}
        />
        <Tile
          icon={<Clock className="size-4" />}
          label={`Unrecovered ${unrecoveredHours}h`}
          value={value(tiles?.unrecovered)}
          sublabel={`no film ${unrecoveredHours} h after approval`}
          tone={tiles && tiles.unrecovered > 0 ? "alert" : "default"}
          isPending={isPending}
          unavailable={unavailable}
        />
        <Tile
          icon={<Timer className="size-4" />}
          label="Median time to film"
          value={formatMinutes(tiles?.median_minutes_to_film)}
          sublabel="approval → film, recovered ones"
          isPending={isPending}
          unavailable={unavailable}
        />
      </div>
      {causes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Causes ({windowLabel(days)}):</span>
          {causes.map((row) => (
            <Badge key={row.cause} variant={row.variant}>
              {row.label} · {row.count}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function Tile({
  icon,
  label,
  value,
  sublabel,
  tone = "default",
  isPending,
  unavailable,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sublabel: string;
  tone?: "default" | "alert";
  isPending: boolean;
  unavailable: boolean;
}) {
  return (
    <Card className="gap-2 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <span aria-hidden>{icon}</span>
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4">
        {isPending ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <div
            className={cn(
              "text-2xl font-semibold tabular-nums",
              tone === "alert" && "text-destructive",
              unavailable && "text-muted-foreground/60",
            )}
          >
            {unavailable ? "—" : value}
          </div>
        )}
        <div className="text-xs text-muted-foreground">{sublabel}</div>
      </CardContent>
    </Card>
  );
}

// --- Sections ----------------------------------------------------------------

function SectionCard({
  title,
  icon,
  count,
  children,
}: {
  title: string;
  icon: ReactNode;
  count?: number | null;
  children: ReactNode;
}) {
  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="text-muted-foreground" aria-hidden>
            {icon}
          </span>
          {title}
          {count != null && (
            <Badge variant="secondary" className="tabular-nums">
              {count}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function SectionError({ error }: { error: unknown }) {
  return (
    <p role="status" className="text-sm text-muted-foreground">
      Could not load this section
      {error instanceof Error && error.message ? `: ${error.message}` : ""}. It
      retries automatically every minute.
    </p>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((row) => (
        <Skeleton key={row} className="h-8 w-full" />
      ))}
    </div>
  );
}

function EmptyRow({ colSpan, text }: { colSpan: number; text: string }) {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        className="py-6 text-center text-sm text-muted-foreground"
      >
        {text}
      </TableCell>
    </TableRow>
  );
}

// --- Cells -------------------------------------------------------------------

function CustomerCell({ incident }: { incident: EchoIncident }) {
  return (
    <div className="min-w-28">
      <div className="font-medium">{customerName(incident)}</div>
      <div className="text-xs tabular-nums text-muted-foreground">
        {customerDetail(incident)}
      </div>
    </div>
  );
}

function EchoCell({ incident }: { incident: EchoIncident }) {
  const occasion = occasionLabel(incident.occasion);
  return (
    <div className="min-w-36">
      <div className="font-medium">{echoLabel(incident)}</div>
      <div className="text-xs text-muted-foreground">
        {[occasion, shortWorkloadId(incident.workload_id)]
          .filter(Boolean)
          .join(" · ")}
        {incident.phase === "interview" ? " · during interview" : ""}
        {incident.phase === "edit" ? " · edit of a delivered film" : ""}
      </div>
    </div>
  );
}

function CauseCell({
  incident,
  overdueMinutes,
}: {
  incident: EchoIncident;
  overdueMinutes: number;
}) {
  const detail = incident.cause_detail || incident.film_error;
  return (
    <div className="min-w-40 max-w-72 space-y-1">
      <Badge
        variant={causeVariant(incident.cause)}
        title={incidentCauseDescription(incident, overdueMinutes)}
      >
        {incidentCauseLabel(incident)}
      </Badge>
      {detail ? (
        <div
          className="line-clamp-2 whitespace-normal text-xs text-muted-foreground"
          title={detail}
        >
          {detail}
        </div>
      ) : null}
    </div>
  );
}

function LinksCell({ incident }: { incident: EchoIncident }) {
  const links = incidentLinks(incident);
  const linkClass =
    "text-xs font-medium underline-offset-4 hover:underline text-primary";
  return (
    <div className="flex min-w-44 flex-wrap gap-x-3 gap-y-1 whitespace-nowrap">
      <Link to={links.echo} className={linkClass}>
        Echo
      </Link>
      <Link to={links.studio} className={linkClass}>
        Echo Studio
      </Link>
      {links.chat ? (
        <Link to={links.chat} className={linkClass}>
          Chat
        </Link>
      ) : null}
      {links.original ? (
        <Link to={links.original} className={linkClass}>
          Original film
        </Link>
      ) : null}
    </div>
  );
}

// --- Tables ------------------------------------------------------------------

function OlderOpenNotice({
  count,
  windowDays,
  shown,
  onToggle,
}: {
  count: number;
  windowDays: number;
  shown: boolean;
  onToggle: () => void;
}) {
  const older = `${count} older ${count === 1 ? "incident" : "incidents"} that opened before ${windowPhrase(windowDays)} ${count === 1 ? "is" : "are"} still open`;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span>{shown ? `Including ${older}.` : `${older}.`}</span>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        onClick={onToggle}
      >
        {shown ? "Hide them" : "Show them"}
      </Button>
    </div>
  );
}

function RecoveryCell({
  incident,
  now,
}: {
  incident: EchoIncident;
  now: number;
}) {
  const recovery = recoveryView(incident, now);
  const links = incidentLinks(incident);
  return (
    <div className="min-w-44 max-w-64 space-y-0.5 text-xs">
      <div
        className={cn(
          "font-medium",
          recovery.kind === "none" && "text-muted-foreground",
        )}
        title={
          recovery.at ? `Started ${formatTimestamp(recovery.at)}` : undefined
        }
      >
        {links.retry ? (
          <Link
            to={links.retry}
            className="text-primary underline-offset-4 hover:underline"
          >
            {recovery.label}
          </Link>
        ) : (
          recovery.label
        )}
      </div>
      {recovery.status ? (
        <div
          className={cn(
            "text-muted-foreground",
            recovery.tone === "failed" && "text-destructive",
          )}
        >
          {recovery.status}
        </div>
      ) : null}
    </div>
  );
}

function OpenIncidentsTable({
  incidents,
  now,
  overdueMinutes,
  windowDays,
}: {
  incidents: EchoIncident[];
  now: number;
  overdueMinutes: number;
  windowDays: number;
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Echo</TableHead>
            <TableHead>Cause</TableHead>
            <TableHead>Approved</TableHead>
            <TableHead>Waiting</TableHead>
            <TableHead>Recovery</TableHead>
            <TableHead>Customer told?</TableHead>
            <TableHead>WhatsApp window</TableHead>
            <TableHead>Open in</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {incidents.length === 0 ? (
            <EmptyRow
              colSpan={9}
              text={`Nothing opened in ${windowPhrase(windowDays)} is still open. Every approved Echo has its film or is still within its ${overdueMinutes} minutes.`}
            />
          ) : (
            incidents.map((incident) => {
              const notice = customerNotice(incident);
              const chatWindow = windowState(incident.window_expires_at, now);
              return (
                <TableRow key={incident.workload_id} className="align-top">
                  <TableCell>
                    <CustomerCell incident={incident} />
                  </TableCell>
                  <TableCell>
                    <EchoCell incident={incident} />
                  </TableCell>
                  <TableCell>
                    <CauseCell
                      incident={incident}
                      overdueMinutes={overdueMinutes}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {formatTimestamp(incident.approved_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium tabular-nums">
                    {formatAge(waitingSince(incident), now)}
                  </TableCell>
                  <TableCell>
                    <RecoveryCell incident={incident} now={now} />
                  </TableCell>
                  <TableCell className="text-xs">
                    <span
                      className={cn(
                        notice.kind === "failure" && "text-destructive",
                        notice.kind === "none" && "text-muted-foreground",
                      )}
                      title={notice.at ? formatTimestamp(notice.at) : undefined}
                    >
                      {notice.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        chatWindow.kind === "open" ? "secondary" : "outline"
                      }
                      className={cn(
                        chatWindow.kind === "closed" && "text-muted-foreground",
                      )}
                      title={
                        chatWindow.kind === "unknown"
                          ? "The contact profile has no window expiry"
                          : `${chatWindow.kind === "open" ? "Open until" : "Closed since"} ${formatTimestamp(chatWindow.until)}`
                      }
                    >
                      {chatWindow.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <LinksCell incident={incident} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function HistoryTable({
  incidents,
  overdueMinutes,
}: {
  incidents: EchoIncident[];
  overdueMinutes: number;
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Echo</TableHead>
            <TableHead>Cause</TableHead>
            <TableHead>Outcome</TableHead>
            <TableHead>Approved</TableHead>
            <TableHead>Time to film</TableHead>
            <TableHead>Recovered by</TableHead>
            <TableHead>Open in</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {incidents.length === 0 ? (
            <EmptyRow
              colSpan={8}
              text="No incidents were resolved or went unrecovered in this window."
            />
          ) : (
            incidents.map((incident) => {
              const links = incidentLinks(incident);
              const minutes = timeToFilmMinutes(incident);
              return (
                <TableRow key={incident.workload_id} className="align-top">
                  <TableCell>
                    <CustomerCell incident={incident} />
                  </TableCell>
                  <TableCell>
                    <EchoCell incident={incident} />
                  </TableCell>
                  <TableCell>
                    <CauseCell
                      incident={incident}
                      overdueMinutes={overdueMinutes}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Badge variant={stateVariant(incident.state)}>
                        {stateLabel(incident.state)}
                      </Badge>
                      {incident.state === "resolved" ? (
                        <div className="text-xs text-muted-foreground">
                          {resolutionLabel(incident.resolution)}
                          {incident.resolved_at
                            ? ` · ${formatTimestamp(incident.resolved_at)}`
                            : ""}
                        </div>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {formatTimestamp(incident.approved_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {incident.state === "unrecovered" ? (
                      <span className="text-destructive">No film</span>
                    ) : (
                      formatMinutes(minutes)
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {links.recovery && incident.recovery_workload_id ? (
                      <Link
                        to={links.recovery}
                        className="font-medium text-primary underline-offset-4 hover:underline"
                      >
                        Echo {shortWorkloadId(incident.recovery_workload_id)}
                      </Link>
                    ) : incident.resolution === "delivered_same_echo" ? (
                      <span className="text-muted-foreground">This Echo</span>
                    ) : incident.resolution === "delivered_remake" ? (
                      <span className="text-muted-foreground">
                        Remake {shortWorkloadId(incident.recovery_workload_id)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <LinksCell incident={incident} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function OutcomesTable({
  summary,
}: {
  summary: EchoIncidentSummary | undefined;
}) {
  const rows = outcomeWeekRows(summary?.customer_film_outcomes?.weeks);
  const totals = outcomeTotalsRow(summary?.customer_film_outcomes?.totals);
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Week</TableHead>
            <TableHead className="text-right">Customers approved</TableHead>
            <TableHead className="text-right">Film within 72 h</TableHead>
            <TableHead className="text-right">Without film</TableHead>
            <TableHead className="text-right">Pending</TableHead>
            <TableHead className="text-right">Without ÷ decided</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={6} text="No approvals in these weeks yet." />
          ) : (
            rows.map((row) => <OutcomeRow key={row.key} row={row} />)
          )}
          {totals && rows.length > 0 ? (
            <OutcomeRow row={totals} emphasis />
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}

function OutcomeRow({
  row,
  emphasis = false,
}: {
  row: OutcomeWeekRow;
  emphasis?: boolean;
}) {
  return (
    <TableRow className={cn(emphasis && "bg-muted/40 font-medium")}>
      <TableCell className="whitespace-nowrap">
        {row.week}
        {row.weekStart ? (
          <span className="ml-2 text-xs text-muted-foreground">
            from {row.weekStart}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-right tabular-nums">{row.approved}</TableCell>
      <TableCell className="text-right tabular-nums">{row.withFilm}</TableCell>
      <TableCell
        className={cn(
          "text-right tabular-nums",
          row.without > 0 && "text-destructive",
        )}
      >
        {row.without}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {row.pending}
      </TableCell>
      <TableCell
        className="text-right tabular-nums"
        title={`${row.without} of ${row.decided} decided customers${row.incomplete ? "; some are still pending" : ""}`}
      >
        {row.rate}
        <span className="ml-1 text-xs text-muted-foreground">
          ({row.without}/{row.decided})
        </span>
      </TableCell>
    </TableRow>
  );
}
